import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, FileText, Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Health Card', path: '/health-card' },
    { name: 'Services', path: '/services' },
    { name: 'Specialties', path: '/specialties' },
    { name: 'Scans & Diagnostics', path: '/lab-tests' },
    { name: 'Health Packages', path: '/health-packages' },
    { name: 'Pharmacy', path: '/pharmacy' },
    { name: 'Corporate Health', path: '/corporate' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
            <img
              src="https://customer-assets.emergentagent.com/job_amc-care/artifacts/rt3uhs8c_AW_name%20board_curve%202X4.pdf%20%284%29.png"
              alt="AMC Logo"
              className="h-12 w-12 object-contain"
            />
            <div className="hidden lg:block">
              <div className="text-[#0A2540] font-bold text-sm leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Aaric Medical Center
              </div>
              <div className="text-[#0EA5E9] text-[10px] tracking-wider font-semibold">AMC for your health</div>
            </div>
            <div className="lg:hidden">
              <div className="text-[#0A2540] font-bold text-base leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Aaric Medical Center
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive(link.path)
                    ? 'text-[#0EA5E9] bg-[#0EA5E9]/10'
                    : 'text-[#475569] hover:text-[#0A2540] hover:bg-slate-50'
                }`}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center space-x-3">
            <a href="tel:6380608090" data-testid="header-call-now-button">
              <Button className="bg-[#0EA5E9] hover:bg-[#0EA5E9]/90 text-white rounded-full px-5 py-2 font-semibold">
                <Phone className="h-4 w-4 mr-2" />
                Call Now
              </Button>
            </a>
            <a href="tel:7010200869" data-testid="header-emergency-call-button">
              <Button className="bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-full px-5 py-2 font-semibold animate-pulse shadow-lg">
                <Phone className="h-4 w-4 mr-2" />
                Emergency
              </Button>
            </a>
            <Link to="/download-reports">
              <Button
                variant="outline"
                className="border-2 border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540]/5 rounded-full px-5 py-2"
                data-testid="download-reports-button"
              >
                <FileText className="h-4 w-4 mr-2" />
                Reports
              </Button>
            </Link>
          </div>

          <div className="lg:hidden flex items-center space-x-2">
            <a href="tel:7010200869" data-testid="header-mobile-call-button">
              <Button className="bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-full px-4 py-2 font-semibold text-sm">
                <Phone className="h-4 w-4 mr-1" />
                Call
              </Button>
            </a>
            <button
              className="text-[#0A2540] p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden pb-4" data-testid="mobile-menu">
            <nav className="flex flex-col space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    isActive(link.path)
                      ? 'text-[#0EA5E9] bg-[#0EA5E9]/10'
                      : 'text-[#475569] hover:text-[#0A2540] hover:bg-slate-50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>
            <div className="mt-4 flex flex-col space-y-2">
              <a href="tel:6380608090" data-testid="mobile-call-now-button">
                <Button className="w-full bg-[#0EA5E9] hover:bg-[#0EA5E9]/90 text-white rounded-full font-semibold">
                  <Phone className="h-4 w-4 mr-2" />
                  Call Now: 6380608090
                </Button>
              </a>
              <a href="tel:7010200869" data-testid="mobile-emergency-call-button">
                <Button className="w-full bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-full font-semibold animate-pulse">
                  <Phone className="h-4 w-4 mr-2" />
                  Emergency Call
                </Button>
              </a>
              <Link to="/download-reports">
                <Button
                  variant="outline"
                  className="w-full border-2 border-[#0A2540] text-[#0A2540] rounded-full"
                  data-testid="mobile-download-reports-button"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Download Reports
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;